import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    AutocompleteFunction,
    BaseAdvancedSearchSandbox,
    BaseNestedFieldDefinition,
    DottedKeyToFlatKey,
    ExtendedBaseProfileField,
    ExtendedProfileService,
    FieldType,
    FormSelectOption,
    Group,
    MultiFormSelectOptions,
    ShAutocompleteService,
    User,
    ValueType,
    formSelectOptionSorter,
    getAutocompleteFctHelper,
    multiFormSelectOptionWrapper
} from '@stylehaven/components';
import { uniqWith } from 'lodash-es';
import { ThesaurusService } from '../../common/services/thesaurus/thesaurus.service';
import { UserStoreService } from '../../common/services/user/user-store.service';

@Injectable({ providedIn: 'root' })
export class AdvancedSearchSandboxService extends BaseAdvancedSearchSandbox<BaseNestedFieldDefinition> {
    constructor(
        protected userStoreService: UserStoreService,
        protected translateService: SharedLanguageService,
        protected thesaurusService: ThesaurusService,
        protected autocompleteService: ShAutocompleteService,
        protected profileService: ExtendedProfileService
    ) {
        super();
    }

    protected getThesaurusOptions(
        profileField: ExtendedBaseProfileField,
        searchTerm?: string | undefined
    ): Observable<MultiFormSelectOptions<string>> {
        return this.thesaurusService.getThesaurusUriFieldResults(profileField.FieldDefinition, searchTerm);
    }

    protected getDefaultMultiSelectOptions(
        profileField: ExtendedBaseProfileField,
        searchTerm?: string | undefined
    ): Observable<MultiFormSelectOptions<string>> {
        const flatKey: string = DottedKeyToFlatKey(profileField.FieldDefinition.DottedKey);
        return this.mapGroupToSelectOption(flatKey);
    }

    private mapGroupToSelectOption(groupKey: string): Observable<MultiFormSelectOptions> {
        return this.userStoreService
            .getUserGroups()
            .pipe(map(groups => multiFormSelectOptionWrapper(this.groupKeyToOptionList(groupKey, groups))));
    }

    private groupKeyToOptionList(groupKey: string, groups: Group[]): FormSelectOption<string>[] {
        let optionList: FormSelectOption[] = [];

        switch (groupKey) {
            case 'PermissionsRead':
            case 'PermissionsWrite':
            case 'PermissionsExport':
                optionList = groups.map(group => ({ label: group.Name, value: group.Id }));
                break;
            case 'DepartmentName':
                optionList = groups.map(group => ({ label: group.Name, value: group.Name }));
                break;
            default:
                break;
        }
        return optionList.sort((group1, group2) => group1.label.localeCompare(group2.label));
    }

    public profileFieldsToTypeSelectOptions(profileFields: ExtendedBaseProfileField[]): FormSelectOption[] {
        return profileFields.map(this.profileFieldToTypeSelectOption).sort(formSelectOptionSorter);
    }

    public profileFieldToTypeSelectOption(profileField: ExtendedBaseProfileField): FormSelectOption {
        return { label: profileField.DisplayLabel, value: profileField.FieldDefinition.DottedKey };
    }

    public getProfileFieldsStream(): Observable<ExtendedBaseProfileField[]> {
        return this.userStoreService.getCurrentUser().pipe(
            map((user: User) => user.Organisation.Id),
            switchMap(orgId => this.profileService.getAdvancedSearchProfileFields(orgId)),
            map(fields => fields.filter(field => !!field.FieldDefinition && field.Public)),
            map(fields =>
                uniqWith(
                    fields,
                    (field1: ExtendedBaseProfileField, field2: ExtendedBaseProfileField) =>
                        field1.FieldDefinition.DottedKey === field2.FieldDefinition.DottedKey
                )
            ),
            shareReplay(1)
        );
    }

    public getValueType(profileField: ExtendedBaseProfileField): ValueType {
        switch (profileField.FieldDefinition.Type) {
            case FieldType.EnumField:
            case FieldType.BooleanField:
            case FieldType.ThesaurusUriField:
                return ValueType.Option;
            case FieldType.LongField:
                return ValueType.Number;
            case FieldType.DateField:
                return ValueType.Date;
            case FieldType.TextField:
                return ValueType.LongText;
            default:
                switch (profileField.FieldDefinition.DottedKey) {
                    case 'Administrative.DepartmentName':
                    case 'RightsManagement.Permissions.Read':
                    case 'RightsManagement.Permissions.Write':
                    case 'RightsManagement.Permissions.Export':
                        return ValueType.Group;
                    default:
                        return ValueType.Text;
                }
        }
    }

    public getAutocompleteFct(dottedKey: string): AutocompleteFunction | undefined {
        return getAutocompleteFctHelper(dottedKey, this.autocompleteService);
    }

    protected getTranslationOrDefault(key: string, fallback: string): string {
        const translated: string = this.translateService.instant(key);
        return translated === key ? fallback : translated;
    }
}
